import { LitElement, css, html } from 'lit';
import { SettingsChangeEvent, TagCategoryDataType, TopicCategory, UserTag } from '../../../types';
import { customElement, state } from 'lit/decorators';
import { instanceOfTagGroup, moveItem } from '../../../helpers/tags-categories';

import { StateController } from '../../../helpers/state/state-management/state-controller';
import { createTopicRequestFromTopic } from '../../../helpers/api/topics-api';
import { dispatchSettingsUpdate } from '../../../helpers/settings';
import topicData from '../../../helpers/state/topic-data';
import { getMainPage } from '../../../helpers/page-name';

@customElement('tags-categories-wrapper-mobile')
export class TagsCategoriesWrapperMobile extends LitElement {
    amplitude_callsite: string = 'Edit Categories Drawer';



    @state()
    activeTab: TagCategoryDataType = TagCategoryDataType.Category;

    @state()
    activeData?: TopicCategory | UserTag;

    @state()
    activeDataType: TagCategoryDataType = TagCategoryDataType.Category;

    @state()
    showAddData: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);

    toggleTab(type: TagCategoryDataType) {
        this.activeTab = type;
        this.activeDataType = type;

        this.activeData = undefined;
        this.showAddData = false;
    }

    onEditClick(data: TopicCategory | UserTag) {
        this.activeData = data;
        if (instanceOfTagGroup(data)) {
            this.activeDataType = TagCategoryDataType.TagGroup;
        }
        this.showAddData = true;
    }

    deleteData() {
        const dataToDelete = this.activeData;
        let settingsChangeEvent: SettingsChangeEvent;

        if (this.activeDataType === TagCategoryDataType.Category) {
            settingsChangeEvent = {
                field: 'categories',
                updatedValue: topicData.categories.filter(c => c._id !== dataToDelete?._id)
            };
        } else {
            settingsChangeEvent = {
                field: 'tag_info',
                updatedValue: {
                    user_tags: topicData.topLevelUserTags.filter(c => c._id !== dataToDelete?._id)
                }
            };
        }

        dispatchSettingsUpdate(
            settingsChangeEvent,
            this
        );

        // delete the data
        this.activeData = undefined;
        this.showAddData = false;
    }

    async onAddClick(dataType: TagCategoryDataType) {
        if (dataType === TagCategoryDataType.TagGroup) {
            this.activeDataType = dataType;
            this.activeData = await topicData.createNewUserTag(true, this.amplitude_callsite);
            this.showAddData = true;
        } else if (dataType === TagCategoryDataType.Tag) {
            this.activeDataType = dataType;
            this.activeData = await topicData.createNewUserTag(false, this.amplitude_callsite);
            this.showAddData = true;
        } else {
            this.activeDataType = dataType;
            this.activeData = await topicData.createNewCategory(this.amplitude_callsite);
            this.showAddData = true;
        }
    }

    async onUpClick(index: number, dataType: TagCategoryDataType) {
        const data = dataType === TagCategoryDataType.Category ? topicData.categories : topicData.topLevelUserTags;
        moveItem(data, index, index - 1);

        const updatedTopic = {
            ...createTopicRequestFromTopic(topicData.topic),
        };

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await topicData.fetchAndUpdateTopicData(updatedTopic, this.amplitude_callsite);
        }, 'An error has occurred.  Context: could not reorder the categories.');
    }

    async onDownClick(index: number, dataType: TagCategoryDataType) {
        const data = dataType === TagCategoryDataType.Category ? topicData.categories : topicData.topLevelUserTags;
        moveItem(data, index, index + 1);

        const updatedTopic = {
            ...createTopicRequestFromTopic(topicData.topic),
        };

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await topicData.fetchAndUpdateTopicData(updatedTopic, this.amplitude_callsite);
        }, 'An error has occurred.  Context: could not reorder the categories.');
    }

    selectTag(e: CustomEvent) {
        const activeIndex = e.detail.tabIndex;
        this.activeDataType = activeIndex === 0 ? TagCategoryDataType.Category : TagCategoryDataType.Tag;
        this.toggleTab(this.activeDataType);
    }

    exitEditMode() {
        this.activeData = undefined;
        this.showAddData = false;
    }

    protected render() {
        return html`
            <div class="top-wrapper">
                <tabs-component .options=${['Categories', 'Tags']} @on-select-tab=${this.selectTag}></tabs-component>
                ${this.activeData ? html`
                    <form-button
                        size="xs"
                        @click=${this.exitEditMode}
                    >
                        Back
                    </form-button>
                    ` : ''}
            </div>
            ${this.showAddData ?
                html`<edit-categories-tags
                        .showSave="${false}"
                        .dataType="${this.activeDataType}"
                        .data="${this.activeData}">
                    </edit-categories-tags>`
            : this.activeTab === TagCategoryDataType.Category ?
                html`


                    <tags-categories-item
                        .data="${topicData.categories}"
                        .dataType="${TagCategoryDataType.Category}"
                        @on-down-click="${async(e: CustomEvent) => await this.onDownClick(e.detail.index, e.detail.dataType)}"
                        @on-up-click="${async(e: CustomEvent) => await this.onUpClick(e.detail.index, e.detail.dataType)}"
                        @on-edit-click="${(e: CustomEvent) => this.onEditClick(e.detail.data)}">
                    </tags-categories-item>
                    <form-button
                        icon="add"
                        size="sm"
                        raised
                        ?disabled="${topicData.categories.length >= 50}"
                        class="tags-categories__add"
                        @click="${async () => await this.onAddClick(TagCategoryDataType.Category)}">
                        <mwc-icon class="tags-categories__sell">add</mwc-icon>
                        Add ${this.activeDataType}
                    </form-button>
                    `
                : html`

                    <tags-categories-item
                        .data="${topicData.topLevelUserTags}"
                        .dataType="${TagCategoryDataType.Tag}"
                        @on-down-click="${async (e: CustomEvent) => await this.onDownClick(e.detail.index, e.detail.dataType)}"
                        @on-up-click="${async (e: CustomEvent) => await this.onUpClick(e.detail.index, e.detail.dataType)}"
                        @on-edit-click="${(e: CustomEvent) => this.onEditClick(e.detail.data)}">
                    </tags-categories-item>
                    <span class="tags-categories__add-wrapper">
                        <form-button
                            size="sm"
                            raised
                            icon="add"
                            class="tags-categories__add"
                            @click="${async () => await this.onAddClick(TagCategoryDataType.Tag)}">
                            Add Tag
                        </form-button>
                        <form-button
                            size="sm"
                            raised
                            icon="add"
                            class="tags-categories__add"
                            @click="${async () => await this.onAddClick(TagCategoryDataType.TagGroup)}">
                            Add Tag Group
                        </form-button>
                    </span>
                    `
            }

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            mwc-button {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
            }

            .wrapper {
                display: flex;
                flex-direction: column;
            }

            .tags-categories__sell {
                --mdc-icon-size: 14px;
                vertical-align: text-bottom;
            }

            .tags-categories__sell--group {
                margin-left: -11px;
            }

            .top-wrapper {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
            }

            .tags-categories__add {
                display: block;
                text-align: center;
                margin: 10px;
                margin-bottom: 0;
            }

            .tags-categories__add-wrapper {
                display: flex;
            }

            .tags-categories__item {
                background-color: white;
                border: 1px solid lightgray;
                border-radius: 50px;
                padding: 8px;
                margin: 10px 4px 5px;
                filter: drop-shadow(rgba(0, 0, 0, 0.25) 0px 4px 4px);
            }

            .tags-categories__item.over {
                outline: 2px dotted #666;
            }

            .tags-categories__item.tags-categories__item--group {
                background-color: #EFECF3;
            }

            .tags-categories__item.active {
                border: 3px solid var(--main-primary-color);
            }

            .tags-categories__item--image {
                vertical-align: text-bottom;
            }
        `;
    }

}
